import React, { memo } from 'react';
import PropTypes from 'prop-types';

const SurfFishingReport = ({ waterTemp, weather, processedTides, moonData }) => {
  return (
    <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur rounded-xl shadow-lg p-5 border border-orange-100 dark:border-gray-700 animate-on-scroll card-hover hover-lift">
      <h3 className="font-semibold text-lg mb-4 dark:text-gray-100">🎣 Surf Fishing Report</h3>
      
      {/* Current Conditions for Fishing */}
      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-lg p-4 mb-4">
        <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">Current Surf Conditions</h4>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-gray-600 dark:text-gray-400">Water Temp:</p>
            <p className="font-medium text-lg text-blue-700 dark:text-blue-300">
              {waterTemp ? `${Math.round(waterTemp)}°F` : '--°F'}
            </p>
          </div>
          <div>
            <p className="text-gray-600 dark:text-gray-400">Wind:</p>
            <p className="font-medium text-lg text-blue-700 dark:text-blue-300">
              {weather?.current?.wind_speed_10m ? `${Math.round(weather.current.wind_speed_10m)} mph` : '--'}
            </p>
          </div>
          <div>
            <p className="text-gray-600 dark:text-gray-400">Next Tide:</p>
            <p className="font-medium text-blue-700 dark:text-blue-300">
              {processedTides.length > 0 && processedTides[0] ? (
                <>
                  {processedTides[0].isHigh ? 'High' : 'Low'} at {processedTides[0].tideTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                </>
              ) : '--'}
            </p>
          </div>
          <div>
            <p className="text-gray-600 dark:text-gray-400">Moon Phase:</p>
            <p className="font-medium text-blue-700 dark:text-blue-300">
              {moonData?.current ? `${moonData.current.emoji} ${moonData.current.illumination}%` : '--'}
            </p>
          </div>
        </div>
      </div>
      
      {/* Fishing Forecast */}
      <div className="space-y-3">
        <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 hover-lift">
          <p className="font-medium text-green-800 dark:text-green-200 mb-1">🟢 Best Times Today</p>
          <ul className="text-sm text-green-700 dark:text-green-300 space-y-1">
            <li>• Dawn: First 2 hours after sunrise (excellent)</li>
            <li>• Dusk: 2 hours before sunset (very good)</li>
            <li>• High tide ± 2 hours (good for predators)</li>
          </ul>
        </div>
        
        <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
          <p className="font-medium text-yellow-800 dark:text-yellow-200 mb-1">🟡 Current Target Species</p>
          <ul className="text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
            <li>• Pompano: {waterTemp && waterTemp >= 68 && waterTemp <= 75 ? 'Prime conditions!' : 'Look for warmer days'}</li>
            <li>• Redfish: Active in the surf zone</li>
            <li>• Spanish Mackerel: {waterTemp && waterTemp >= 70 ? 'Moving through!' : 'Water too cool'}</li>
            <li>• Whiting: Always biting on shrimp</li>
          </ul>
        </div>
        
        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <p className="font-medium text-blue-800 dark:text-blue-200 mb-1">🎣 Recommended Tackle</p>
          <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
            <li>• Sand fleas or fresh shrimp for pompano</li>
            <li>• Gotcha plugs for Spanish mackerel</li>
            <li>• Fish bites or shrimp for whiting</li>
            <li>• Cut bait or live bait for redfish</li>
          </ul>
        </div>
      </div>
      
      {/* External Links */}
      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">External Fishing Resources:</p>
        <div className="flex gap-2 flex-wrap">
          <a 
            href="https://www.saltstrong.com/fishing-spots/florida/port-st-joe/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
          >
            Salt Strong Reports
          </a>
          <span className="text-gray-400">•</span>
          <a 
            href="https://www.fishingweather.com/forecast/florida/cape-san-blas"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
          >
            Fishing Weather
          </a>
          <span className="text-gray-400">•</span>
          <a 
            href="https://tides4fishing.com/us/florida-gulf-coast/cape-san-blas"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
          >
            Solunar Tables
          </a>
        </div>
      </div>
    </div>
  );
};

SurfFishingReport.propTypes = {
  waterTemp: PropTypes.number,
  weather: PropTypes.shape({
    current: PropTypes.shape({
      wind_speed_10m: PropTypes.number
    })
  }),
  processedTides: PropTypes.arrayOf(PropTypes.shape({
    tideTime: PropTypes.instanceOf(Date),
    isHigh: PropTypes.bool
  })),
  moonData: PropTypes.shape({
    current: PropTypes.shape({
      emoji: PropTypes.string,
      illumination: PropTypes.number
    })
  })
};

export default memo(SurfFishingReport);